import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faPencil } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TripContext } from 'contexts'
import { SelectBox } from 'devextreme-react'
import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import { OrderItem } from 'services/types'
import './ti-hi-editor.scss'

type TiHiField = 'ti' | 'hi'

type Props = {
    orderItems: OrderItem[]
}

const isValidTiHiValue = (value: number | null): value is number => value !== null && Number.isInteger(value) && value > 0 && value <= 999

const getSelectableValues = (value: number | null): (number | string)[] => {
    if (!isValidTiHiValue(value)) {
        return ['N/A']
    }

    return ['N/A', ...Array.from({ length: 21 }, (_, index) => value - 10 + index).filter(item => item > 0 && item <= 999)]
}

export const TiHiEditor: FC<Props> = ({ orderItems }) => {
    const tripContext = useContext(TripContext)
    const primaryItem = orderItems[0]
    const [tiValue, setTiValue] = useState<number | null>(primaryItem?.ti ?? null)
    const [hiValue, setHiValue] = useState<number | null>(primaryItem?.hi ?? null)
    const [savedTiValue, setSavedTiValue] = useState<number | null>(primaryItem?.ti ?? null)
    const [savedHiValue, setSavedHiValue] = useState<number | null>(primaryItem?.hi ?? null)
    const [isEditing, setIsEditing] = useState(false)
    const [fieldErrors, setFieldErrors] = useState<Record<TiHiField, string | null>>({ ti: null, hi: null })
    const rangeBase = useRef({ itemId: primaryItem?.id, ti: primaryItem?.ti ?? null, hi: primaryItem?.hi ?? null })

    if (rangeBase.current.itemId !== primaryItem?.id) {
        rangeBase.current = { itemId: primaryItem?.id, ti: primaryItem?.ti ?? null, hi: primaryItem?.hi ?? null }
    }

    useEffect(() => {
        setTiValue(primaryItem?.ti ?? null)
        setHiValue(primaryItem?.hi ?? null)
        setSavedTiValue(primaryItem?.ti ?? null)
        setSavedHiValue(primaryItem?.hi ?? null)
        setIsEditing(false)
        setFieldErrors({ ti: null, hi: null })
    }, [primaryItem?.id, primaryItem?.ti, primaryItem?.hi])

    if (!primaryItem) {
        return null
    }

    const cancelEdit = (): void => {
        setTiValue(savedTiValue)
        setHiValue(savedHiValue)
        setFieldErrors({ ti: null, hi: null })
        setIsEditing(false)
    }

    const saveEdit = (): void => {
        const tiChanged = tiValue !== savedTiValue
        const hiChanged = hiValue !== savedHiValue
        if (fieldErrors.ti || fieldErrors.hi) {
            return
        }
        if ((tiChanged && tiValue !== null && !isValidTiHiValue(tiValue)) ||
            (hiChanged && hiValue !== null && !isValidTiHiValue(hiValue))) {
            setFieldErrors({
                ti: tiChanged && tiValue !== null && !isValidTiHiValue(tiValue) ? 'Ti must be a whole number between 1 and 999.' : null,
                hi: hiChanged && hiValue !== null && !isValidTiHiValue(hiValue) ? 'Hi must be a whole number between 1 and 999.' : null
            })
            return
        }

        const updatedItems = orderItems.map(item => ({
            ...item,
            ...(tiChanged && { ti: tiValue, tiWasUpdated: true }),
            ...(hiChanged && { hi: hiValue, hiWasUpdated: true })
        }))

        if (tiChanged || hiChanged) {
            tripContext.orderItems.bulkPut(updatedItems)
        }
        setSavedTiValue(tiValue)
        setSavedHiValue(hiValue)
        setFieldErrors({ ti: null, hi: null })
        setIsEditing(false)
    }

    const renderField = (field: TiHiField, label: string, value: number | null) => {
        const selectableValues = getSelectableValues(rangeBase.current[field])
        const setValue = field === 'ti' ? setTiValue : setHiValue

        return (
            <SelectBox
                className={'ti-hi-editor-select'}
                elementAttr={{ id: `ti-hi-${field}-select` }}
                inputAttr={{
                    'aria-label': label,
                    'aria-invalid': !!fieldErrors[field],
                    'aria-describedby': fieldErrors[field] ? `ti-hi-${field}-error` : undefined
                }}
                items={selectableValues}
                acceptCustomValue={true}
                searchEnabled={true}
                dropDownOptions={{ wrapperAttr: { class: 'ti-hi-editor-dropdown' } }}
                noDataText={'Type a whole number between 1 and 999'}
                placeholder={'Enter or select'}
                stylingMode={'outlined'}
                value={isValidTiHiValue(value) ? value : 'N/A'}
                onCustomItemCreating={event => {
                    const text = String(event.text ?? '').trim()
                    const parsedValue = Number(text)
                    if (isValidTiHiValue(parsedValue)) {
                        event.customItem = parsedValue
                        setValue(parsedValue)
                        setFieldErrors(errors => ({ ...errors, [field]: null }))
                    } else {
                        event.customItem = null
                        setFieldErrors(errors => ({ ...errors, [field]: `${label} must be a whole number between 1 and 999.` }))
                    }
                }}
                onValueChanged={event => {
                    // Rejecting a custom item emits a null value change. Only the explicit
                    // N/A option may clear a value; rejection must preserve the field error.
                    if (event.value === 'N/A' || isValidTiHiValue(event.value)) {
                        setValue(event.value === 'N/A' ? null : event.value)
                        setFieldErrors(errors => ({ ...errors, [field]: null }))
                    }
                }}
            />
        )
    }

    if (!isEditing) {
        const displayValue = `${isValidTiHiValue(savedTiValue) ? savedTiValue : 'N/A'} / ${isValidTiHiValue(savedHiValue) ? savedHiValue : 'N/A'}${primaryItem.palletSize?.trim() ? ` ${primaryItem.palletSize}` : ''}`

        return (
            <div className={'ti-hi-editor'}>
                <div className={'ti-hi-editor-readonly item-detail-info-row-default'}>
                    <div className={'item-detail-info-title'}>Ti / Hi</div>
                    <div className={'item-detail-info-colon'}>:</div>
                    <div className={'item-detail-info-value-left ti-hi-editor-value'}>{displayValue}</div>
                    <button
                        aria-label={'Edit Ti and Hi'}
                        className={'ti-hi-editor-pencil'}
                        type={'button'}
                        onClick={() => {
                            setTiValue(savedTiValue)
                            setHiValue(savedHiValue)
                            setFieldErrors({ ti: null, hi: null })
                            setIsEditing(true)
                        }}
                    >
                        <FontAwesomeIcon icon={faPencil as IconProp} aria-hidden={'true'} />
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className={'ti-hi-editor ti-hi-editor-editing'}>
            <div className={'ti-hi-editor-row'}>
                <div className={'ti-hi-editor-label'}>Ti</div>
                <div className={'ti-hi-editor-control'}>{renderField('ti', 'Ti', tiValue)}</div>
            </div>
            {fieldErrors.ti && <div id={'ti-hi-ti-error'} role={'alert'} className={'ti-hi-editor-error'}>{fieldErrors.ti}</div>}
            <div className={'ti-hi-editor-row'}>
                <div className={'ti-hi-editor-label'}>Hi</div>
                <div className={'ti-hi-editor-control'}>{renderField('hi', 'Hi', hiValue)}</div>
            </div>
            {fieldErrors.hi && <div id={'ti-hi-hi-error'} role={'alert'} className={'ti-hi-editor-error'}>{fieldErrors.hi}</div>}
            <div className={'ti-hi-editor-actions'}>
                <button className={'ti-hi-editor-cancel'} type={'button'} onClick={cancelEdit}>Cancel</button>
                <button className={'ti-hi-editor-save'} type={'button'} disabled={!!fieldErrors.ti || !!fieldErrors.hi} onClick={saveEdit}>Save</button>
            </div>
        </div>
    )
}
